import { useState } from 'react';
import { PhoneCall, GraduationCap, ClipboardList, FolderOpen, Construction } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import DocumentsManager from '../components/DocumentsManager.jsx';

// 👥 EMPLOYEE — internal-use area. Three sections, each backed by the WORKING
// document + notes upload (DocumentsManager) so the AI uses the admin's uploaded
// material on the relevant internal calls:
//   • Communication — call employees (outbound to an employee list). The dialer
//     reuse is scaffolded; the notes/docs the AI uses on those calls are live.
//   • Training      — training content the AI delivers (Learner-style tutor).
//   • Onboarding    — new-hire material.
//
// The CENTERPIECE is the document/notes upload: each section files its docs under
// its own `area`, and the LiveKit agent injects active+inject rows into the call
// prompt. Communication/Training/Onboarding delivery flows are scaffolded.

const TABS = [
  {
    id: 'communication',
    label: 'Communication',
    icon: PhoneCall,
    area: 'employee:communication',
    docTitle: 'Communication notes & documents',
    docBlurb: 'Material the AI uses when calling employees — announcements, scripts, policy reminders.',
    scaffold: {
      title: 'Call employees (outbound)',
      body: 'Reuses the campaign dialer against an employee contact list. Build an employee list and trigger an outbound run — the AI uses the notes/documents below as context.',
      next: 'Wire an employee contact-list picker + a one-click outbound run on top of the existing dialer.'
    }
  },
  {
    id: 'training',
    label: 'Training',
    icon: GraduationCap,
    area: 'employee:training',
    docTitle: 'Training content & notes',
    docBlurb: 'Upload the training material the AI teaches from — guides, SOPs, product docs.',
    scaffold: {
      title: 'Training delivery (tutor)',
      body: 'A single-voice tutor (Learner-style) walks an employee through the uploaded training material on a call.',
      next: 'Add a "start a training call" launcher that runs the solo-tutor agent primed with this section\'s documents.'
    }
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    icon: ClipboardList,
    area: 'employee:onboarding',
    docTitle: 'Onboarding material & notes',
    docBlurb: 'New-hire material the AI references — handbook excerpts, first-week checklists, benefits docs.',
    scaffold: {
      title: 'New-hire onboarding flow',
      body: 'A guided onboarding call/checklist the AI runs a new hire through, drawing on the documents below.',
      next: 'Add a structured onboarding checklist + a "send onboarding call" trigger.'
    }
  }
];

export default function Employee() {
  const [tabId, setTabId] = useState('communication');
  const tab = TABS.find((t) => t.id === tabId);

  return (
    <div className="px-6 md:px-10 py-8 max-w-4xl">
      <PageHeader
        title="Employee"
        subtitle="Internal-use AI — communication, training, and onboarding for your team"
      />

      <div className="flex items-center gap-2 mb-6 border-b border-stone-900">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTabId(id)}
            className={`flex items-center gap-2 px-3 py-2 text-sm -mb-px border-b-2 ${
              tabId === id
                ? 'border-alfred-gold text-stone-100'
                : 'border-transparent text-stone-400 hover:text-stone-200'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* Scaffolded delivery flow — clearly marked as a next step. */}
      <div className="rounded-xl border border-stone-800 bg-stone-900/40 p-4 mb-8">
        <div className="flex items-center gap-2 mb-1">
          <FolderOpen size={16} className="text-alfred-gold" />
          <h3 className="text-stone-100 font-medium text-sm">{tab.scaffold.title}</h3>
        </div>
        <p className="text-sm text-stone-400">{tab.scaffold.body}</p>
        <div className="mt-3 inline-flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
          <Construction size={14} className="text-amber-300 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-200">
            <strong>Next step:</strong> {tab.scaffold.next}
          </p>
        </div>
      </div>

      {/* The working centerpiece: documents + notes the AI uses. */}
      <DocumentsManager area={tab.area} title={tab.docTitle} blurb={tab.docBlurb} />
    </div>
  );
}
